import React, { useState, useEffect, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import {
  Grid,
  Avatar,
  Paper,
  Typography,
  Card,
  IconButton,
  Box,
  Tooltip
} from "@mui/material";
import {
  PageTitleWithToolTip,
  PrimaryButton,
  SecondaryButton,
} from "../Common";
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import "./../../custom.css";
import Routes from "../../routes";
import { Black, Grey1, Grey2, Grey3, Grey6, darkGray, green, purple, teal, trakrefBlue, trakrefYellow, white } from "../../color_utils";
import {
  trakrefFontFamilySemiBold,
  trakrefFontFamilyBold,
  AppLanguage,
  trakrefFontFamily,
  dateTimeAgo,
  renderMessageWithMentions
} from "../../utils";
import CircularLoading from "../Common/CircularLoading";
import toast from "react-hot-toast";
import Edit from "@mui/icons-material/BorderColorOutlined";
import UserInfo from "../../UserInfo";
import { Mention, MentionsInput } from "react-mentions";
import { Assets, Clipboard, Location, ServiceEvents } from "../../Icons/Icons";
import debounce from "lodash.debounce";

const Discussion = ({ itemID, type, initialCommentId, isFocusText }) => {
  const [adding, setAdding] = useState(false);
  const [comment, setComment] = useState("");
  const [allowSearch, setAllowSearch] = useState(false);
  const [data, setData] = useState([]);
  const [isFocusFirstTime, setIsFocusFirstTime] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [editingComment, setEditingComment] = useState(null);
  const [scrollCommentID, setScrollCommentID] = useState(null);
  const [userInformation, setUserInformation] = useState({
    id: 0,
    name: "",
    shortName: "",
    headshot: "",
  });
  const inputRef = useRef(null);
  const editInputRef = useRef(null);
  const commentRefs = useRef([]);

  const fetchUser = async () => {
    const response = await fetch(Routes.User.GetUserDetails(UserInfo.userId));
    const user = await response.json();

    const newUser = {
      ...user,
      language: AppLanguage.find((x) => x.id === user.language).name,
    };

    setUserInformation({ ...userInformation, ...newUser });
    setIsLoading(false);
  };

  const fetchComment = async () => {
    try {
      const response = await fetch(Routes.Comment.GetComments(type, itemID));
      if (!response.ok) {
        throw new Error("Failed to fetch comments");
      }
      const data = await response.json();
      console.table(data);

      if (Array.isArray(data)) {
        setData(data.sort((a, b) => new Date(b.commentDate) - new Date(a.commentDate)));
      }

      setIsLoading(false);
    } catch (error) {
      console.error(error);
    }
  };

  /**
   * Extract Tagged Users in the comment string
   * @param {string | undefined} comment 
   * @returns  {any[]}
   */
  const getTaggedUsers = (comment) => {
    const userIdRegex = /\/User\/(\d+)/g; // Regular expression to capture all sets of digits inside parentheses
    const taggedUsers = [];

    let match;
    while ((match = userIdRegex.exec(comment)) !== null) {
      const userId = match[1];
      if (!taggedUsers.includes(userId)) {
        taggedUsers.push(userId);
      }
    }

    return taggedUsers;
  };

  const addComment = () => {
    if (!comment.trim()) {
      toast.error('Please enter comment.', { duration: 6000 });
      return;
    }

    if (adding) return;
    setAdding(true);

    const commentData = {
      itemID: itemID,
      type: type,
      note: comment,
      userId: UserInfo.userId,
      taggedUsers: getTaggedUsers(comment)
    };

    const url = Routes.Comment.AddComment();
    const requestOptions = {
      method: "POST",
      body: JSON.stringify(commentData),
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    };
    fetch(url, requestOptions)
      .then(() => {
        fetchComment();
      })
      .finally(() => {
        setComment("");
        setAdding(false);
      })
      .catch((err) => {
        toast.error('Error Creating Comment', { duration: 6000 });
        console.log('Error', err);
      });
  };

  const deleteComment = (commentId) => {
    const commentData = {
      itemID: itemID,
      type: type,
      note: comment,
      commentId: commentId,
      userId: userInformation.id,
      taggedUsers: getTaggedUsers(comment)
    };
    const url = Routes.Comment.DeleteComment();
    const requestOptions = {
      method: "PUT",
      body: JSON.stringify(commentData),
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    };
    fetch(url, requestOptions)
      .then(() => {
        fetchComment();
        toast.success('The comment has been successfully deleted.', { duration: 6000 });
      })
      .catch((err) => {
        toast.error('Error deleted Comment', { duration: 6000 });
        console.log('Error', err);
      });
  };

  const editComment = (commentId) => {
    const commentData = {
      itemID: itemID,
      type: type,
      note: comment,
      commentId: commentId,
      userId: UserInfo.userId,
      taggedUsers: getTaggedUsers(comment)
    };
    const url = Routes.Comment.EditComment();
    const requestOptions = {
      method: "PUT",
      body: JSON.stringify(commentData),
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    };
    fetch(url, requestOptions)
      .then(() => fetchComment())
      .finally(() => {
        toast.success('The comment has been successfully updated.', { duration: 6000 });
        setEditingComment(null);
        setComment("");
      })
      .catch((err) => {
        toast.error('Error edited Comment', { duration: 6000 });
        console.log('Error', err);
      });
  };

  useEffect(() => {
    fetchComment();
    fetchUser();

    // We want to scroll to the designated comment only once
    setScrollCommentID(initialCommentId);
  }, []);

  useEffect(() => {
    if (isFocusText && inputRef.current && !isFocusFirstTime) {
      inputRef.current.focus();
      setIsFocusFirstTime(true);
    }
  }, [isLoading]);

  useEffect(() => {
    if (data.length > 0 && scrollCommentID) {
      commentRefs.current[scrollCommentID].scrollIntoView({
        behavior: "smooth",
        block: "center",
      });

      // We want to scroll to the designated comment only once, so need to empty it after it scroll
      setScrollCommentID(null);
    }

  }, [data]);

  const handleChange = (ev) => {
    // filter the text and keep only the tagging data
    const filterPreviousTag = /@\[[^\]]*\]\([^\)]*\)\s*/g;
    const searchWord = ev.target.value.replace(filterPreviousTag, "");
    const findTag = /[@#][a-zA-Z\d]+(\s[a-zA-Z\d]+)+$/;
    const wordMatches = searchWord.match(findTag) || [];
    setAllowSearch(wordMatches.length > 0 ? true : false);
    setComment(ev.target.value);
  };
  const handleCancel = () => {
    setComment("");
    setEditingComment(null);
  };

  const handleEditClick = (commentId, commentText) => {
    setEditingComment(commentId);
    setComment(commentText);
    if (editInputRef.current) {
      editInputRef.current.focus();
    }
  };
  const handleSaveEdit = (commentId) => {
    editComment(commentId, comment);
  };

  /**
   * 
   * @param {string} query 
   * @returns  { {
   * isLoading: boolean;
   * keyword: string;
   * options: any;
   * workOrders: any[];
   * assets: any[];
   * locations: any[];
   * serviceEvents: any[];
   * users: any[];
   * open: boolean;
   * }}
   */
  const searchData = async (query) => {
    if (query.length < 1) {
      return null;
    }
    return await fetch(Routes.Dashboard.Search(query), {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(query),
    })
      .then((response) => response.json())
      .then((json) => {
        const newState = {
          isLoading: false,
          keyword: query,
          options: json,
          workOrders: dataFilter(json, "WorkOrder"),
          assets: dataFilter(json, "Inventory"),
          locations: dataFilter(json, "Location"),
          serviceEvents: dataFilter(json, "ServiceEvent"),
          users: dataFilter(json, "User"),
          open: true,
        };
        return newState;
      })
      .catch((err) => console.log("Error fetching options", err));
  };

  /**
   * @param {{
   *    id: string | undefined,
   *    display: string | undefined,
   *    text: string | undefined,
   *    link: string | undefined,
   *    locationName: string | undefined,
   *    status: string | undefined,
   *    woNumber: string | undefined,
   *    code: string | undefined,
   *    serviceType: string | undefined,
   *    avatarUrl: string | undefined
   *  }} suggestion 
   * @returns 
   */
  const UserSearchResult = (suggestion, index) => {
    const bgColor = avatarColors[index % avatarColors.length];
    return (
      <Grid
        container
        minWidth={"250px"}
        sx={{
          alignItems: "center",
        }}
      >
        <div className="ResultItemContainer flex f-row f-ac">
          {suggestion.avatarUrl ? (
            <img className="greenImageExtraSmall" src={suggestion.avatarUrl} />
          ) : (
            <div className="greenCircleExtraSmall" style={{ backgroundColor: bgColor }}>
              {suggestion.userShortName}
            </div>
          )}

          <Typography
            sx={{
              marginLeft: "8px",
              marginRight: "8px",
              borderRadius: "5px",
              display: "flex",
              fontFamily: trakrefFontFamily
            }}
          >
            <div>
              {/* <div className="AssetText"> */}
              <p style={{ color: Black, fontSize: "0.813rem", fontFamily: trakrefFontFamilySemiBold }}>{suggestion.resultTitle}</p>
              <p style={{ color: Grey1, fontSize: "0.688rem", letterSpacing: "0px" }}>{suggestion.locationName}</p>
            </div>
          </Typography>
        </div>
      </Grid>
    );
  };

  /**
   * @param {{
   *    id: string| undefined,
   *    display: string| undefined,
   *    text: string| undefined,
   *    link: string| undefined,
   *    locationName: string| undefined,
   *    status: string| undefined,
   *    woNumber: string| undefined,
   *    code: string| undefined,
   *    serviceType: string| undefined,
   *    avatarUrl: string| undefined
   *  }} suggestion 
   * @returns {JSX.Element}
   */
  const ItemsSearchResult = (suggestion) => {
    return (
      <Grid
        container
        minWidth={"700px"}
        sx={{
          alignItems: "center",
          overflow: 'visible'
        }}
      >
        <div className="ResultItemContainer flex f-row f-ac">
          <Grid item>
            {getItemIcon(suggestion.link)}
          </Grid>
          <Grid
            item
            sx={{
              marginLeft: "8px",
              marginRight: "8px",
              borderRadius: "5px",
              display: "flex",
            }}
          >
            <Typography
              sx={{
                display: 'inline-block',
                fontSize: "15px",
                fontWeight: "500",
                fontFamily: trakrefFontFamily,
                color: trakrefBlue,
                letterSpacing: "-1px",
              }}>{getItemDescription(suggestion.link)} {suggestion.text}</Typography>
            <Typography
              sx={{
                display: 'inline-block',
                fontSize: "15px",
                fontWeight: "500",
                fontFamily: trakrefFontFamily,
                color: Black,
                letterSpacing: "-1px",
              }}>: {getSubDescription(suggestion)} </Typography>
          </Grid>
        </div>
      </Grid>
    );
  };

  const getSubDescription = (suggestion) => {
    if (suggestion.link.includes("WorkOrder") || suggestion.link.includes("Location")) {
      return `${suggestion.locationName} • ${suggestion.status}`;
    } else if (suggestion.link.includes("Inventory")) {
      return suggestion.status;
    } else if (suggestion.link.includes("ServiceEvent")) {
      return `Work Order #${suggestion.woNumber} • Work Order Status: ${suggestion.woStatus} `;
    } else {
      return "";
    }
  };

  /**
   * 
   * @param {string | undefined} link 
   * @returns  {string | ""}
   */
  const getItemDescription = (link) => {
    if (link.includes("WorkOrder")) {
      return "Work Order";
    } else if (link.includes("Inventory")) {
      return "Asset";
    } else if (link.includes("Location")) {
      return "Location";
    } else if (link.includes("ServiceEvent")) {
      return "Service Event";
    } else if (link.includes("User")) {
      return "User";
    } else {
      return "";
    }
  };

  /**
   * 
   * @param {string | undefined} link 
   * @returns  {JSX.Element | undefined}
   */
  const getItemIcon = (link) => {
    if (link.includes("WorkOrder")) {
      return <Clipboard size={"24px"} color={trakrefBlue} />;
    } else if (link.includes("Inventory")) {
      return <Assets size={"24px"} color={green} />;
    } else if (link.includes("Location")) {
      return <Location size={"24px"} color={trakrefBlue} />;
    } else if (link.includes("ServiceEvent")) {
      return <ServiceEvents size={"24px"} color={trakrefYellow} />;
    }
  };

  /**
   * 
   * @param {any[]} items 
   * @returns  {{
   * display: string |undefined
   * id: string | undefined
   * link: string | undefined
   * locationName: string | undefined
   * status: string | undefined
   * woNumber: string | undefined
   * code: string | undefined
   * }}
   */
  const MentionResultFormat = (items) => {
    return items.map(
      (e) => (
        {
          display: e.display,
          text: e.text,
          resultTitle: (e.link ? e.link : "").includes("User") ? e.text : e.display,
          id: e.link,
          link: e.link,
          locationName: e.locationName,
          status: e.status,
          woNumber: e.woNumber,
          woStatus: e.woStatus,
          code: e.code,
          avatarUrl: e.avatarUrl,
          userShortName: e.userShortName
        }
      )
    );
  };

  /**
   * 
   * @param {string | undefined} json 
   * @param {string | undefined} key 
   * @returns  {{
   *    id: string | undefined,
   *    display: string | undefined,
   *    text: string | undefined,
   *    link: string | undefined,
   *    locationName: string | undefined,
   *    status: string | undefined,
   *    woNumber: string | undefined,
   *    code: string | undefined,
   *    serviceType: string | undefined,
   *    avatarUrl: string | undefined
   *  }[]}
   */
  function dataFilter(json, key) {
    return json ? json.filter((obj) => obj.link.includes(key)) : null;
  }

  const fetchUserList = useCallback(
    debounce(
      (search, callBack) => {
        // Here's callback for loading
        callBack(
          [{
            display: "loading_display",
            id: "loading_id",
            isLoading: true
          }]
        );
        searchData(search).then((newState) => {
          if (newState) {
            callBack(
              MentionResultFormat(newState.users)
            );
          } else {
            callBack([]);
          }
        });
      }, 1000
    ), []
  );

  const fetchItemList = useCallback(
    debounce(
      (search, callBack) => {
        // Here's callback for loading
        callBack(
          [{
            display: "loading_display",
            id: "loading_id",
            isLoading: true
          }]
        );
        searchData(search).then((newState) => {
          if (newState) {
            callBack(
              [
                ...MentionResultFormat(newState.assets),
                ...MentionResultFormat(newState.serviceEvents),
                ...MentionResultFormat(newState.workOrders),
                ...MentionResultFormat(newState.locations),
              ]
            );
          } else {
            callBack([]);
          }
        }
        );
      }, 1000
    ), []
  );

  const addPermission = () => {
    if (UserInfo.isSystemAdministrator()) return true;

    if (UserInfo.isNativeSystemOwner()) return !UserInfo.isReadOnly();

    return UserInfo.isTenantAdministrator() || UserInfo.isTechnicianManager() || UserInfo.isTechnician();
  };

  const editPermission = (comment) => {
    if (UserInfo.isSystemAdministrator()) {
      return true;
    }

    if (userInformation.id !== comment.userId) {
      return UserInfo.isNativeSystemOwner() && UserInfo.isSuperTenantAdmin();
    }

    return (UserInfo.isNativeServiceProvider() && (UserInfo.isTenantAdministrator())) ||
      (UserInfo.isNativeSystemOwner() && (UserInfo.isSuperTenantAdmin() || UserInfo.isTenantAdministrator() || UserInfo.isRegionalAdministrator()));
  };

  const deletePermission = (comment) => {
    if (UserInfo.isSystemAdministrator()) {
      return true;
    }

    if (userInformation.id !== comment.userId) return UserInfo.isNativeSystemOwner() && (UserInfo.isSuperTenantAdmin() || UserInfo.isTenantAdministrator());

    return (UserInfo.isNativeServiceProvider() && UserInfo.isTenantAdministrator()) ||
      (UserInfo.isNativeSystemOwner() && (UserInfo.isSuperTenantAdmin() || UserInfo.isTenantAdministrator()));
  };

  const avatarColors = [trakrefBlue, trakrefYellow, purple];

  return (
    <>
      {isLoading ? (
        <CircularLoading />
      ) : (
        <Paper
          sx={{
            p: 2,
            margin: "auto",
            flexGrow: 1,
            backgroundColor: (theme) => { return (theme.palette.mode === "dark") ? darkGray : white; },
          }}
        >
          <Box marginLeft={4}>
            <Grid container>
              <Grid item xs={6} md={6} lg={6} mb={3}>
                <PageTitleWithToolTip
                  pageTitle={"Discussion"}
                  toolTipNode={
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <span>Use Discussion to add comment(s) to an attribute. Comments can be used to give additional information about an attribute.</span>
                      <span>Users can tag other team members and link to inventory, work order(s), or service event(s).</span>
                    </div>
                  }
                  fontSize="18px"
                />
              </Grid>
            </Grid>
            {!editingComment && renderCommentInput(inputRef)}
            
    
            {data &&
              data.length > 0 &&
              data.map((comment, index) => (
                RenderComment(comment, index)
              ))}
          </Box>
        </Paper>
      )}
    </>
  );

  function renderCommentInput(edittingRef) {
    return  <>
        {/* Comment Input Box */}
        {addPermission() && (
            <Grid container mb={2}>
                <Grid item mr={1}>
                    {userInformation.headshot ? (
                        <Avatar
                            src={userInformation.headshot}
                            alt="userAvatar"
                            sx={{ width: 28, height: 28 }} />
                    ) : (
                        <Avatar
                            alt={userInformation.name}
                            sx={{
                                width: 28,
                                height: 28,
                                backgroundColor: teal,
                                fontSize: "13px",
                                fontFamily: trakrefFontFamilySemiBold,
                            }}
                        >
                            {userInformation.shortName}
                        </Avatar>
                    )}
                </Grid>
                <MentionsInput
                    inputRef={edittingRef}
                    style={{
                        width: "70%",
                        height: "91px",
                        "&multiLine": {
                            control: {
                                fontFamily: trakrefFontFamily,
                                minHeight: 63
                            },
                            highlighter: {
                                paddingLeft: "9px",
                                paddingTop: "2px",
                                border: "1px solid transparent",
                                height: 91,
                                maxHeight: '16rem',
                                overflow: 'hidden',
                                paddingRight: '15px',
                                boxSizing: 'border-box',
                                lineHeight: 'normal',
                                resize: 'none',
                                bottom: 0,
                                fontFamily: trakrefFontFamily,
                            },
                            input: {
                                border: `1px solid ${Grey6}`,
                                paddingLeft: "9px",
                                paddingTop: "2px",
                                borderRadius: "6px",
                                height: 91,
                                maxHeight: '16rem',
                                bottom: 0,
                                fontFamily: trakrefFontFamily,
                                resize: 'none',
                                paddingRight: '15px',
                                boxSizing: 'border-box',
                                lineHeight: 'normal',
                            }
                        },
                        suggestions: {
                            list: {
                                border: `1px solid ${Grey6}`,
                            },
                        },
                        fontSize: "1rem",
                    }}
                    value={comment}
                    onChange={handleChange}
                    customSuggestionsContainer={(children) => {
                        return <div className="searchResultContainer searchTag">
                            {children}
                        </div>;
                    }}
                    allowSpaceInQuery={allowSearch}
                >
                    <Mention
                        style={{
                            color: trakrefBlue,
                            zIndex: 1,
                            position: "relative",
                        }}
                        trigger="@"
                        data={fetchUserList}
                        renderSuggestion={(suggestion, _, __, index) => {
                            if (suggestion.isLoading) {
                                return <Grid container
                                    minWidth={"250px"}
                                    justifyContent="center"
                                    alignItems="center"
                                >
                                    <Grid item>
                                        <CircularLoading marginLeft='0px' />
                                    </Grid>
                                </Grid>;
                            } else {
                                return UserSearchResult(suggestion, index);
                            }
                        }} />
                    <Mention
                        trigger="#"
                        renderSuggestion={(suggestion) => {
                            if (suggestion.isLoading) {
                                return <Grid container
                                    minWidth={"250px"}
                                    justifyContent="center"
                                    alignItems="center"
                                >
                                    <Grid item>
                                        <CircularLoading marginLeft='0px' />
                                    </Grid>
                                </Grid>;
                            } else {
                                return ItemsSearchResult(suggestion);
                            }
                        }}
                        data={fetchItemList} />
                </MentionsInput>
                {(comment.length === 0) &&
                    <Typography
                        sx={{
                            zIndex: 2,
                            width: "55%",
                            marginTop: "8px",
                            marginLeft: "46px",
                            position: "absolute",
                            fontSize: "0.813rem",
                            fontStyle: "italic",
                            color: Grey3,
                            fontFamily: trakrefFontFamily,
                            lineHeight: "1.106rem",
                            pointerEvents: "none",
                        }}
                    >
                        Add a comment. Use # to link a work order or an asset or @ to mention a person.
                    </Typography>}
            </Grid>
        )}

        {/* Comment Action Button */}
        
        {
            comment.length > 0 && addPermission() ? (
                <Grid
                    marginLeft={4.5}
                    container
                    mb={2}
                    justifyContent={"flex-end"}
                    spacing={2}
                    width={"70%"}
                >
                    <Grid item>
                        <SecondaryButton buttonText="Cancel" onClick={handleCancel} />
                    </Grid>
                    <Grid item>
                        <PrimaryButton
                            buttonText="Save"
                            onClick={!editingComment
                                ? addComment
                                : () => handleSaveEdit(editingComment)} />
                    </Grid>
                </Grid>

            ) : null
        }
    </>;
  }

  function RenderComment(comment, index) {
    if (editingComment === comment.commentId) {
      return renderCommentInput(editInputRef);
    }
    const avatarBg = avatarColors[index % avatarColors.length];
    return <Grid key={comment.commentId} container ref={(el) => (commentRefs.current[comment.commentId] = el)}>
      <Grid item mr={1} mt={1}>
        {comment.userAvatarUrl ? (
          <Avatar
            sx={{ width: 28, height: 28 }}
            alt={comment.userDisplayName}
            src={comment.userAvatarUrl} />
        ) : (
          <Avatar
            sx={{
              width: 28,
              height: 28,
              backgroundColor: avatarBg,
              fontSize: "13px",
              fontFamily: trakrefFontFamilySemiBold,
            }}
            alt={comment.userDisplayName}
          >
            {comment.userShortName}
          </Avatar>
        )}
      </Grid>
      <Box sx={{ minWidth: 275, width: "70%", mb: 2 }}>
        <Card variant="outlined" sx={{ p: 2 }}>
          {/* Username */}
          <Grid container mb={2}>
            <Grid container item xs={10} alignItems={"baseline"}>
              <Typography
                sx={{
                  color: Grey2,
                  fontFamily: trakrefFontFamilyBold,
                  fontSize: 15,
                  padding: "13px"
                }}
              >
                {`${comment.userDisplayName}`}
              </Typography>

              <Typography
                sx={{
                  color: Grey2,
                  fontFamily: trakrefFontFamilySemiBold,
                  fontSize: 12
                }}
                ml={2}
              >
                {`Commented ${dateTimeAgo(new Date(comment.commentDate))}`}
              </Typography>
            </Grid>
            <Grid
              item
              xs={2}
              container
              justifyContent="flex-end"
              alignItems="center"
              spacing={0}
            >
              {
                editPermission(comment) && (
                  <Grid item>
                    {editingComment !== comment.commentId ? (
                      <Tooltip title="Edit">
                        <IconButton
                          onClick={() => handleEditClick(
                            comment.commentId,
                            comment.comment
                          )}
                        >
                          <Edit style={{ fontSize: "16px" }} />
                        </IconButton>
                      </Tooltip>
                    ) : null}
                  </Grid>
                )
              }
              {
                deletePermission(comment) && (
                  <Grid item>
                    <Tooltip title="Delete">
                      <IconButton
                        onClick={() => deleteComment(comment.commentId)}
                      >
                        <DeleteOutlineOutlinedIcon style={{ fontSize: "16px" }} />
                      </IconButton>
                    </Tooltip>
                  </Grid>
                )
              }
            </Grid>
          </Grid>

          {/* Comment Detail */}
          <Grid>
            <Typography
              sx={{
                fontFamily: trakrefFontFamily,
                fontSize: 14,
              }}
              variant="body1"
            >
              {renderMessageWithMentions(comment.comment)}
            </Typography>
          </Grid>
        </Card>
      </Box>
    </Grid>;
  }
};

Discussion.propTypes = {
  itemID: PropTypes.string.isRequired,
};

export default Discussion;
